import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, Separator} from 'platform/components';
import {
  Box,
  getSize,
  getValueByDevice,
  Hide,
  Integer,
  Show,
  Space,
  useDevice,
  VStack,
} from 'platform/foundation';

import {isEmpty, isNil, isNotEmpty, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {DesktopPagination} from '../../components/Pagination/DesktopPagination';
import {MobilePaginationWithFilterBar} from '../../components/Pagination/MobilePaginationWithFilterBar';
import {useSavedFiltersPanelHeight} from '../../components/SavedFiltersPanel/hooks/SavedFiltersPanelHeightProvider';
import {VehicleInfo} from '../../components/VehicleInfo/VehicleInfo';
import {useFilter} from '../../hooks/FilterContext';
import {useCatalogue} from '../../hooks/useCatalogue';
import {useHeaderHeight} from '../../hooks/useHeaderHeight';
import {usePriceReport} from '../../hooks/usePriceReport';
import {usePriceReportSearchParams} from '../../hooks/usePriceReportSearchParams';
import i18n from '../../i18n/i18n';
import {
  useGetPriceReportCarsDetailQuery,
  useGetStatisticsV2Query,
} from '../../store/priceReportApi';
import {QueryStateKeys} from '../../utils/routing';
import {StatisticsChart} from './components/Chart/StatisticsChart';
import {addMyVehicleToAllCars} from './utils/addMyVehicleToAllCars';
import {mapNeighboursToStatisticsCars} from './utils/mapNeighboursToStatisticsCars';
import {markMyAd} from './utils/markMyAd';

const ITEMS_PER_PAGE = 1000;

export function StatisticsPage(props: TestIdProps) {
  const device = useDevice();
  const headerHeight = useHeaderHeight();
  const savedFiltersPanelHeight = useSavedFiltersPanelHeight();
  const {filter, errors: filterErrors} = useFilter();
  const {page, setPage} = usePriceReportSearchParams();

  const {priceReport} = usePriceReport();
  const catalogue = useCatalogue();
  const hasServersSelected = isNil(filter?.server) || !isEmpty(filter.server);

  const [debug] = useQueryState(QueryStateKeys.Debug);
  const isDebug = debug === 'true';
  const {data: neighbours, isLoading: areNeighboursLoading} = useGetPriceReportCarsDetailQuery(
    isNotNil(priceReport) &&
      isNotNil(priceReport.neighborAdIds) &&
      isNotEmpty(priceReport.neighborAdIds)
      ? {reportId: priceReport.id, vehicleIds: priceReport.neighborAdIds}
      : skipToken,
    {skip: !isDebug}
  );

  const {data, isLoading, isFetching, isError} = useGetStatisticsV2Query(
    isNotNil(priceReport)
      ? {
          reportId: priceReport.id,
          body: {
            ...filter,
            query_price: priceReport.sellingPrice.recommended.value,
            offset: ((page ?? 1) - 1) * ITEMS_PER_PAGE,
            size: ITEMS_PER_PAGE,
          },
        }
      : skipToken,
    {skip: isNil(filter) || !hasServersSelected}
  );

  const cars = filterErrors || !hasServersSelected ? [] : (data?.cars ?? []);

  const currentPage = data?.current_page ?? 0;
  const pagesQuantity = data?.total_pages ?? 0;
  const results = data?.total_hits ?? 0;

  if (!priceReport) {
    return null;
  }

  const mappedNeighbours = isDebug ? mapNeighboursToStatisticsCars(priceReport, neighbours) : [];
  const mappedStatisticsCars = markMyAd(cars, priceReport);
  const neighbourAdIds = new Set(priceReport.neighborAdIds ?? []);
  // Debug mode re-injects the pricing neighbours enriched, so their plain versions are
  // deduplicated away; in normal mode every car the API returned stays in the chart.
  const competitorCars = isDebug
    ? [
        ...mappedNeighbours,
        ...mappedStatisticsCars.filter((car) => isNil(car.ad_id) || !neighbourAdIds.has(car.ad_id)),
      ]
    : mappedStatisticsCars;

  const carsWithMyVehicle = addMyVehicleToAllCars(
    competitorCars,
    priceReport,
    data?.query_original_price_index ?? 0
  );

  const headerBorder = getValueByDevice(device, '1px', '1px', '0px', '0px');
  const filterBarHeight = getValueByDevice(device, 12, 12, 0, 0);
  const pagePaddings = 2 * 2;
  const cardPaddings = 2 * 4;

  return (
    <>
      <Helmet title={i18n.t('page.statistics.title')} />
      <MobilePaginationWithFilterBar
        page={currentPage}
        onPageChange={setPage}
        pagesQuantity={pagesQuantity}
        results={results}
        isLoading={isFetching || isLoading}
      />
      <Box padding={2}>
        <Card>
          <VStack
            minHeight={`calc(100vh - ${headerHeight}px - ${savedFiltersPanelHeight}px - ${getSize(
              (filterBarHeight + pagePaddings + cardPaddings) as Integer
            )} - ${headerBorder})`}
          >
            <VehicleInfo catalogue={catalogue} vehicle={priceReport.vehicle}>
              <DesktopPagination
                page={currentPage}
                onPageChange={setPage}
                pagesQuantity={pagesQuantity}
                results={results}
                data-testid={suffixTestId('desktopPagination', props)}
              />
            </VehicleInfo>
            <Show onMobile onTablet>
              <Separator />
            </Show>
            <Hide onMobile onTablet>
              <Space vertical={4} />
            </Hide>
            <DataStatus isLoading={isLoading || areNeighboursLoading} isError={isError} grow={1}>
              <VStack grow={1} minHeight={0}>
                <StatisticsChart
                  priceReport={priceReport}
                  data={carsWithMyVehicle}
                  catalogue={catalogue}
                  isLoading={isFetching}
                  data-testid={suffixTestId('statisticsChart', props)}
                />
              </VStack>
            </DataStatus>
          </VStack>
        </Card>
      </Box>
    </>
  );
}
